import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {ResponsiveContainer, BarChart, XAxis, Tooltip as RechartsTooltip, Bar} from 'recharts';

import {createDateRange, DateRangeInterval, getDateRangeInterval, MILLIS_PER_DAY, MILLIS_PER_HOUR} from '../../lib/timeline';
import {getColor} from '../../lib/color';
import {useUser} from '../../lib/user';
import HistoryStack from '../../lib/HistoryStack';
import Menu from '../../components/Menu';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import Tooltip from '../../components/Tooltip';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import DateTimeInput from '../../components/DateTimeInput';

const dateRangeOptions = [
  {label: 'Last hour', range: MILLIS_PER_HOUR},
  {label: 'Last 24 hours', range: MILLIS_PER_DAY},
  {label: 'Last 7 days', range: 6 * MILLIS_PER_DAY},
  {label: 'Last 30 days', range: 29 * MILLIS_PER_DAY},
  {label: 'Custom...', range: -1}
];

function AlertInsights({alerts, formsById, startDate, endDate, onChange: onChangeOriginal}) {
  const user = useUser();
  const [menuOpen, toggleMenu] = useState(null);
  const [showDateRangeModal, toggleDateRangeModal] = useState(false);
  const interval = getDateRangeInterval(startDate, endDate);

  // Track changes in the history stack so that we can push and pop states.
  const history = useRef(new HistoryStack());

  useEffect(() => {
    history.current = new HistoryStack();
    history.current.push([{startDate, endDate}]);
  }, [onChangeOriginal]);

  const onChange = useCallback((...args) => {
    history.current.push(args);
    onChangeOriginal(...args);
  }, [onChangeOriginal]);
  
  const handleBack = useCallback(() => {
    const prev = history.current.back();

    if (prev) {
      onChangeOriginal(...prev);
    }
  }, [onChangeOriginal]);
  
  const handleForward = useCallback(() => {
    const next = history.current.forward();

    if (next) {
      onChangeOriginal(...next);
    }
  }, [onChangeOriginal]);

  // Group the data into date buckets.
  const [data, templateIds] = useMemo(() => {
    const range = createDateRange(startDate, endDate, interval);
    const buckets = range.reduce((acc, next) => {
      acc[next] = {};
      return acc;
    }, {});
    const templateIds = new Set();

    for (let alert of alerts) {
      const bucket = interval.normalize(new Date(alert.date));

      if (!formsById[alert.templateId]) {
        // User does not have access to this template, so skip it.
        continue;
      }
      
      if (buckets[bucket]) {
        if(!buckets[bucket][alert.templateId]) {
          buckets[bucket][alert.templateId] = 0;
        }

        buckets[bucket][alert.templateId]++;
        templateIds.add(alert.templateId);
      }
    }

    /* Data structure needs to look like the following for the stacked bar chart:
     * [
     *   {name: "<date time>", <template ID 1>: <count>, <template ID 2>: <count>, ...},
     *   ... 
     * ]
     */
    const data = range.map(date => ({
      date,
      name: interval.name(date),
      ...buckets[date]
    })).reverse();
    return [data, templateIds];
  }, [alerts, startDate, endDate]);
  
  const handleMenuToggle = useCallback(menuName => menuName ? toggleMenu(menuName) : toggleMenu(null), []);

  const handleDateChange = useCallback(option => {
    if (option.range < 0) {
      toggleDateRangeModal(true);
    } 
    else {
      const now = new Date();
      onChange({startDate: new Date(now.getTime() - option.range), endDate: now});
    }
  }, [onChange]);
  
  const handleBarClick = useCallback(point => {
    if (interval !== DateRangeInterval.MINUTE) {
      onChange({startDate: point.date, endDate: new Date(point.date.getTime() + (interval.difference * 1000))});
    }
  }, [interval, onChange]);
  
  const handleModalClose = useCallback(() => {
    toggleDateRangeModal(false);
  }, []);

  const rangeLabel = `${interval.name(startDate)} - ${interval.name(endDate)}`;

  return (
    <>
      <Container>
        <Header>
          <h1>{user.company.name}</h1>
          <InsightOptions>
            <InverseButton tertiary onClick={handleBack} disabled={!history.current.hasPrevious()}>
              <Icon name="chevron-left" />
            </InverseButton>
            <InverseButton tertiary onClick={handleForward} disabled={!history.current.hasNext()}>
              <Icon name="chevron-right" />
            </InverseButton>
            <InverseButton id="insight-about-icon" tertiary onClick={() => handleMenuToggle('info')} style={{transform: 'rotate(180deg)'}}>
              <Icon name="alert-circle" />
            </InverseButton>
            <InverseButton id="insight-date-range" tertiary onClick={() => handleMenuToggle('dateRange')}>
              {rangeLabel} <Icon name="chevron-down" size="1em" />
            </InverseButton>
            <AboutMenu relativeTo="insight-about-icon" open={menuOpen === 'info'} onClose={handleMenuToggle}>
              <Menu.Text>These are alert insights: they help you stay on top of your operational health by showing alerts over time and identifying outliers.</Menu.Text>
            </AboutMenu>
            <Menu relativeTo="insight-date-range" open={menuOpen === 'dateRange'} onClose={handleMenuToggle}>
              {dateRangeOptions.map((option, index) => (
                <Menu.Option key={index} onClick={() => handleDateChange(option)}>{option.label}</Menu.Option>
              ))}
            </Menu>
          </InsightOptions>
        </Header>
        <ChartContainer>
          <ResponsiveContainer height={250} minWidth={500}>
            <BarChart data={data} barCategoryGap={0}>
              <RechartsTooltip
                content={<CustomTooltip formsById={formsById} />}
                animationDuration={0}
                cursor={{fill: 'rgba(0,0,0,0)'}}
              />
              {[...templateIds].sort().map(templateId => (
                <Bar
                  key={templateId}
                  dataKey={templateId}
                  stackId={0}
                  fill={getColor(templateId)}
                  onClick={handleBarClick}
                  stroke="#101E4B"
                  strokeWidth={2}
                  cursor={interval === DateRangeInterval.MINUTE ? 'default' : 'pointer'}
                />
              ))}
              <XAxis dataKey="name" stroke="#95A0C2" />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
        {templateIds.size < 1 && (
          <NoAlertsContainer>
            <NoAlerts>
              <img src="/assets/no-alerts.svg" alt="" />
              <h4>Alerts will display here</h4>
              <p>Stay on top of your operational health by viewing alerts over time.</p>
            </NoAlerts>
          </NoAlertsContainer>
        )}
      </Container>
      <CustomDateRangeModal
        open={showDateRangeModal}
        startDate={startDate} 
        endDate={endDate} 
        onChange={onChange} 
        onClose={handleModalClose}
      />
    </>
  );
}

function CustomDateRangeModal({open, startDate, endDate, onChange, onClose}) {
  const [dates, setDates] = useState([startDate, endDate]);
  const [invalidRange, setInvalidRange] = useState(false);

  const handleSubmit = useCallback(() => {
    if (Math.abs(dates[1].getTime() - dates[0].getTime()) > 90 * MILLIS_PER_DAY) {
      setInvalidRange(true);
    }
    else {
      setInvalidRange(false);
      onChange({startDate: dates[0], endDate: dates[1]});
      onClose();
    }
  }, [dates, onChange, onClose]);

  // Keep internal state in sync with the props.
  useEffect(() => {
    setDates([startDate, endDate]);
  }, [startDate, endDate]);

  useEffect(() => {
    setInvalidRange(false);
  }, [open]);

  return (
    <Form onSubmit={handleSubmit}>
      <SideModal 
        open={open}
        title="Custom Date Range"
        onClose={onClose}
        options={[
          <Button type="submit">Apply</Button>,
          <Button secondary onClick={onClose}>Cancel</Button>
        ]}>
        <SideModal.Content>
          <Form.Group>
            <DateTimeInput
              id="alert_insights_start_date" 
              label="Start Date &amp; Time"
              invalid={invalidRange}
              date={dates[0]} 
              onChange={date => setDates([date, dates[1]])}
            />
          </Form.Group>
          <Form.Group>
            <DateTimeInput
              id="alert_insights_end_date"
              label="End Date &amp; Time" 
              invalid={invalidRange}
              validationText="The maximum range is 90 days"
              date={dates[1]}
              onChange={date => setDates([dates[0], date])}
            />
          </Form.Group>
        </SideModal.Content>
      </SideModal>
    </Form>
  );
}

function CustomTooltip({active, payload, formsById}) {
  if (!active || !payload || payload.length < 1) {
    return null;
  }

  const total = payload.reduce((acc, point) => acc + point.payload[point.dataKey], 0);

  return (
    <Tooltip>
      <Tooltip.Title>{payload[0].payload.name}</Tooltip.Title>
      <TooltipList>
        {payload.reverse().map(point => (
          <li key={point.dataKey}>
            <ColorLegendDot color={point.color} />
            <span>{formsById[point.dataKey].name}</span>
            {point.payload[point.dataKey]}
          </li>
        ))}
        {payload.length > 1 && (
          <li><TotalLabel>Total</TotalLabel> {total}</li>
        )}
      </TooltipList>
    </Tooltip>
  );
}

const Container = styled.div`
  margin-bottom: 30px;
  padding: 2rem 0 .25rem 0;
  background: #101E4B;
  color: #8185A1;

  @media (max-width: 800px) {
    margin: 20px;
    border-radius: 0.625rem;
    padding: 0 0 .25rem 0;
  }
`;

const Header = styled.div`
  display: flex;
  padding: 0 2rem;
  margin-bottom: 1rem;

  @media (max-width: 800px) {
    justify-content: flex-end;
    padding: 1rem 1rem 0 1rem;
  }
  
  h1 {
    color: white;
    flex: 1;

    @media (max-width: 800px) {
      display: none;
    }
  }
`;

const InsightOptions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const InverseButton = styled(Button)`
  color: #fff;
`;

const ChartContainer = styled.div`
  overflow-x: auto;
  padding: 0 1rem;
`;

const TooltipList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    font-weight: 700;
    color: #666;

    span {
      flex: 1;
      padding-right: 1rem;
      font-weight: 400;
    }
  }
`;

const ColorLegendDot = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  background: ${props => props.color};
  margin-right: 0.25rem;
  margin-top: 2px;
  border-radius: 0.5rem;
`;

const NoAlertsContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
`;

const NoAlerts = styled.div`
  position: absolute;
  top: -220px;
  left: 0;
  right: 0;
  text-align: center;
  color: #CCCBDC;

  img {
    display: block;
    margin: 0 auto;
    width: 100px;
    height: 60px;
  }

  h4 {
    margin: 1rem 0 0.5rem 0;
    font-size: 1.1rem;
  }

  p {
    display: block;
    margin: 0 auto;
    width: 250px;
    font-size: 0.9rem;
  }
`;

const AboutMenu = styled(Menu)`
  max-width: 300px;
`;

const TotalLabel = styled.span`
  font-weight: bold !important;
  padding-left: 1.25rem;
`;

export default AlertInsights;